import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { RyzenMark } from '../components/RyzenLogo';
import api from '../api/client';
import './Login.css';

const TYPING_WORDS = ['smarter.', 'faster.', 'together.', 'efficiently.'];

function TypingText() {
  const [wordIdx, setWordIdx] = useState(0);
  const [displayed, setDisplayed] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [pause, setPause] = useState(false);
  useEffect(() => {
    const word = TYPING_WORDS[wordIdx];
    if (pause) { const t = setTimeout(()=>setPause(false),1200); return()=>clearTimeout(t); }
    if (!deleting) {
      if (displayed.length < word.length) { const t = setTimeout(()=>setDisplayed(word.slice(0,displayed.length+1)),80); return()=>clearTimeout(t); }
      else { const t = setTimeout(()=>setDeleting(true),1800); return()=>clearTimeout(t); }
    } else {
      if (displayed.length > 0) { const t = setTimeout(()=>setDisplayed(displayed.slice(0,-1)),50); return()=>clearTimeout(t); }
      else { setDeleting(false); setPause(true); setWordIdx(i=>(i+1)%TYPING_WORDS.length); }
    }
  },[displayed,deleting,pause,wordIdx]);
  return <span className="lg-accent">{displayed}<span className="lg-cursor">|</span></span>;
}

function FloatingOrbs() {
  const orbs = useMemo(()=>Array.from({length:5}).map((_,i)=>({
    id:i,size:90+i*55,x:8+i*22,y:8+i*20,dur:9+i*3,delay:i*1.4,
    color:i%2===0?'rgba(255,102,0,':'rgba(168,85,247,'
  })),[]);
  return (
    <div className="lg-orbs" aria-hidden="true">
      {orbs.map(o=>(
        <div key={o.id} className="lg-orb" style={{width:o.size,height:o.size,left:o.x+'%',top:o.y+'%',background:o.color+(o.id%2===0?'0.14)':'0.10)'),animationDuration:o.dur+'s',animationDelay:o.delay+'s'}}/>
      ))}
    </div>
  );
}

function Particles() {
  const pts = useMemo(()=>Array.from({length:18}).map((_,i)=>({
    id:i,x:Math.random()*100,y:Math.random()*100,size:2+Math.random()*2,
    dur:5+Math.random()*8,delay:Math.random()*6,op:0.2+Math.random()*0.25,
  })),[]);
  return (
    <div className="lg-particles" aria-hidden="true">
      {pts.map(p=>(
        <div key={p.id} className="lg-pt" style={{left:p.x+'%',top:p.y+'%',width:p.size,height:p.size,opacity:p.op,animationDuration:p.dur+'s',animationDelay:p.delay+'s'}}/>
      ))}
    </div>
  );
}

const EyeIcon   = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>;
const EyeOffIcon= () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>;

export default function Login() {
  const { login, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [mode, setMode] = useState('signin'); // 'signin' | 'reset'
  const [resetKey, setResetKey] = useState('');
  const [resetUser, setResetUser] = useState('');
  const [resetPw, setResetPw] = useState('');
  const [resetMsg, setResetMsg] = useState('');

  const routeFor = (u) => {
    if (!u) return '/dashboard';
    if (u.role === 'User') return '/employee';
    if (u.role === 'Super Admin' && u.onboarded === false) return '/onboarding';
    return '/dashboard';
  };

  useEffect(() => { setMounted(true); }, []);
  useEffect(() => {
    if (isAuthenticated) navigate(routeFor(user));
  }, [isAuthenticated]);

  const handleSignIn = async () => {
    if (!username.trim() || !password.trim()) { setError('Please enter your username and password'); return; }
    setLoading(true); setError('');
    const result = await login(username.trim(), password);
    setLoading(false);
    if (result.success) {
      setTransitioning(true);
      setTimeout(() => {
        const u = JSON.parse(sessionStorage.getItem('ryzenos_session'));
        navigate(routeFor(u));
      }, 650);
    } else { setError(result.error); }
  };

  const handleReset = async () => {
    if (!resetKey.trim() || !resetUser.trim() || !resetPw.trim()) { setError('Enter your license key, username and a new password'); return; }
    if (resetPw.length < 6) { setError('New password must be at least 6 characters'); return; }
    setLoading(true); setError(''); setResetMsg('');
    try {
      await api.auth.resetWithKey(resetKey.trim(), resetUser.trim(), resetPw);
      setResetMsg('Password updated. You can sign in now.');
      setTimeout(() => {
        setMode('signin'); setUsername(resetUser.trim()); setPassword('');
        setResetKey(''); setResetPw(''); setResetMsg('');
      }, 1600);
    } catch (e) {
      setError(e.message || 'Could not reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`lg-wrap${transitioning?' lg-transitioning':''}`}>
      <FloatingOrbs/>
      <Particles/>

      <div className={`lg-card${mounted?' lg-card--in':''}`}>
        <div className="lg-card-bar"/>

        <div className="lg-brand">
          <div className="lg-logo-wrap">
            <RyzenMark size={26}/>
            <div className="lg-logo-ring"/>
          </div>
          <span className="lg-brand-name">RyzenOS</span>
        </div>

        <div className="lg-hero-wrap">
          <div className="lg-hero">Run your business<br/><TypingText/></div>
          <div className="lg-hero-sub">One intelligent platform for your entire team</div>
        </div>

        <div className="lg-divider"/>

        {mode === 'signin' ? (
          <div className="lg-form">
            <div className="lg-signin-label">Sign in to workspace</div>
            <div className="lg-inp-wrap">
              <input className="lg-inp" type="text" placeholder="Username or email" value={username} onChange={e=>setUsername(e.target.value)} onKeyDown={e=>e.key==='Enter'&&handleSignIn()} autoComplete="username"/>
            </div>
            <div className="lg-inp-wrap">
              <input className="lg-inp" type={showPw?'text':'password'} placeholder="Password" value={password} onChange={e=>setPassword(e.target.value)} onKeyDown={e=>e.key==='Enter'&&handleSignIn()} autoComplete="current-password" style={{paddingRight:38}}/>
              <button className="lg-eye-btn" onClick={()=>setShowPw(s=>!s)} type="button">{showPw?<EyeOffIcon/>:<EyeIcon/>}</button>
            </div>
            {error && <div className="lg-error">{error}</div>}
            <button className="lg-btn" onClick={handleSignIn} disabled={loading}>
              {loading?<span className="lg-spinner"/>:'Sign in'}
            </button>
            <button className="lg-key-btn" type="button" onClick={()=>{setMode('reset');setError('');setResetMsg('');}}>
              Forgot password?
            </button>
          </div>
        ) : (
          <div className="lg-form">
            <div className="lg-signin-label">Reset your password</div>
            <div className="lg-inp-wrap">
              <input className="lg-inp" type="text" placeholder="License key (RYZ-…)" value={resetKey} onChange={e=>setResetKey(e.target.value)} autoComplete="off"/>
            </div>
            <div className="lg-inp-wrap">
              <input className="lg-inp" type="text" placeholder="Your username" value={resetUser} onChange={e=>setResetUser(e.target.value)} autoComplete="username"/>
            </div>
            <div className="lg-inp-wrap">
              <input className="lg-inp" type={showPw?'text':'password'} placeholder="New password" value={resetPw} onChange={e=>setResetPw(e.target.value)} onKeyDown={e=>e.key==='Enter'&&handleReset()} autoComplete="new-password" style={{paddingRight:38}}/>
              <button className="lg-eye-btn" onClick={()=>setShowPw(s=>!s)} type="button">{showPw?<EyeOffIcon/>:<EyeIcon/>}</button>
            </div>
            {error && <div className="lg-error">{error}</div>}
            {resetMsg && <div className="lg-error" style={{background:'rgba(34,197,94,0.12)',color:'#16a34a',borderColor:'rgba(34,197,94,0.3)'}}>{resetMsg}</div>}
            <button className="lg-btn" onClick={handleReset} disabled={loading}>
              {loading?<span className="lg-spinner"/>:'Reset password'}
            </button>
            <button className="lg-key-btn" type="button" onClick={()=>{setMode('signin');setError('');setResetMsg('');}}>
              Back to sign in
            </button>
            <div className="lg-hint">Use the license key we sent you to reset your password</div>
          </div>
        )}
      </div>

      <div className="lg-footer">Powered by Ryzenforge Enterprises LLP</div>
    </div>
  );
}
